import React from "react";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mt-6 rounded-2xl bg-white/80 p-4 shadow-sm ring-1 ring-slate-100 sm:p-6">
    <h2 className="text-base font-semibold text-slate-900 sm:text-lg">
      {title}
    </h2>
    <div className="mt-3 text-sm leading-relaxed text-slate-700">
      {children}
    </div>
  </section>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex gap-2 text-sm text-slate-700">
    <span className="mt-[3px] text-emerald-600">•</span>
    <span>{children}</span>
  </li>
);

export const LoveLinkHowPayoutsWork: React.FC = () => {
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <header className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
          LoveLink
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          How LoveLink payouts work
        </h1>
        <p className="mt-2 text-sm text-slate-700">
          LoveLink is not an affiliate hustle. It&apos;s our way of saying{" "}
          <span className="font-medium">thank you</span> when you bring the
          right people into Unity Love — people who respect love, culture,
          and community.
        </p>
      </header>

      <Section title="What you earn">
        <p>
          You earn <span className="font-semibold">10% of what your people spend</span>{" "}
          on Unity Love when they join through your LoveLink and later upgrade.
        </p>
        <p className="mt-3 font-medium text-slate-900">Qualifying purchases can include:</p>
        <ul className="mt-1 space-y-1">
          <Bullet>Elite or premium memberships</Bullet>
          <Bullet>AI Wingman upgrades</Bullet>
          <Bullet>Boosts, lounge time, or special events we run</Bullet>
        </ul>
        <p className="mt-3 text-xs text-slate-600">
          You don&apos;t earn from people who didn&apos;t come in through your
          LoveLink, and we reserve the right to cut off abusive or spammy
          behavior.
        </p>
      </Section>

      <Section title="30-day protection window (Net-30)">
        <p>
          Every earning goes into a{" "}
          <span className="font-semibold">30-day protection window</span>.
          During that time it shows as{" "}
          <span className="font-semibold">Pending (30-day hold)</span> in your
          dashboard.
        </p>
        <p className="mt-3">
          This protects the community and the platform from:
        </p>
        <ul className="mt-1 space-y-1">
          <Bullet>Refunds or chargebacks</Bullet>
          <Bullet>Fraud or misuse</Bullet>
        </ul>
        <p className="mt-3">
          After <span className="font-semibold">30 days</span>, that earning
          automatically moves to{" "}
          <span className="font-semibold">Available for payout</span>. This
          rule is enforced in code — even admins can&apos;t manually bypass
          it.
        </p>
      </Section>

      <Section title="When and how you get paid (V1)">
        <p>
          We&apos;re starting simple and trustworthy with{" "}
          <span className="font-semibold">manual payouts</span>, then
          automating later once volume grows.
        </p>
        <p className="mt-3 font-medium text-slate-900">
          Your LoveLink rewards dashboard shows:
        </p>
        <ul className="mt-1 space-y-1">
          <Bullet>Total received (lifetime)</Bullet>
          <Bullet>Pending (still in the 30-day window)</Bullet>
          <Bullet>Available (ready for payout after Net-30)</Bullet>
          <Bullet>Paid out (funds you&apos;ve already received)</Bullet>
        </ul>
        <p className="mt-3">
          We will announce payout windows and collect your payout details
          (Cash App, Zelle, or other supported options). Once the payout is
          sent, those rewards are marked as{" "}
          <span className="font-semibold">Paid</span> in your dashboard, with
          a reference note for your records.
        </p>
        <p className="mt-3 text-sm text-slate-700">
          In a future version, we&apos;ll move to{" "}
          <span className="font-semibold">automatic payouts</span> using a
          provider like Stripe Connect once the program is battle-tested and
          payout volume is high enough.
        </p>
      </Section>

      <Section title="What this is — and what it isn't">
        <p>
          To be crystal clear about the spirit behind this:
        </p>
        <ul className="mt-2 space-y-1">
          <Bullet>
            LoveLink is <span className="font-semibold">not</span> an MLM or
            get-rich-quick scheme.
          </Bullet>
          <Bullet>
            LoveLink is <span className="font-semibold">not</span> a promise
            of a job replacement — it&apos;s a community reward.
          </Bullet>
          <Bullet>
            LoveLink <span className="font-semibold">is</span> a way to say{" "}
            <span className="font-semibold">"thank you"</span> when you help
            us grow a healthy, respectful community.
          </Bullet>
          <Bullet>
            LoveLink <span className="font-semibold">is</span> for people who
            actually believe in the space they&apos;re sharing.
          </Bullet>
        </ul>
        <p className="mt-3 text-xs text-slate-600">
          We reserve the right to disable LoveLink rewards for spam,
          harassment, fake accounts, or anything that violates community
          guidelines.
        </p>
      </Section>

      <Section title="Quick FAQ">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Do I get paid for free signups?
            </p>
            <p className="mt-1 text-sm text-slate-700">
              No. Rewards are based on{" "}
              <span className="font-semibold">qualifying purchases</span>{" "}
              your referrals make after joining through your LoveLink.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Can I see where my rewards came from?
            </p>
            <p className="mt-1 text-sm text-slate-700">
              Yes. Your rewards view includes recent activity with product
              labels, dates, and referral emails where allowed.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Is there a minimum payout amount?
            </p>
            <p className="mt-1 text-sm text-slate-700">
              In V1 we may group payouts to keep things efficient. As the
              program grows, we&apos;ll publish any minimums (for example,
              <span className="font-semibold"> $50</span>) clearly on this
              page.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Can my LoveLink rewards be disabled?
            </p>
            <p className="mt-1 text-sm text-slate-700">
              Yes. Abuse, spam, fake accounts, or violations of Unity Love
              guidelines can lead to LoveLink rewards being limited or
              disabled. This is about{" "}
              <span className="font-semibold">healthy</span> growth, not
              aggressive tactics.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};
